// Run/observability surfaces: task queue, pagination, activity log
// (WEB-8004). Shell surfaces — composed by workspaceStyles.ts in place.
export const runsTaskQueue = `
/* ===== Task queue — header ===== */
.ws-tasks-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 28px 32px 0; max-width: 1100px; margin: 0 auto; width: 100%; box-sizing: border-box;
}
.ws-tasks-header-left { display: flex; flex-direction: column; gap: 2px; }
.ws-tasks-title {
  font-family: var(--ws-serif); font-size: 28px; font-weight: 700;
  color: var(--ws-ink); margin: 0; line-height: 1.2;
}
.ws-tasks-subtitle {
  font-size: 13px; color: var(--ws-ink-4); font-family: var(--ws-sans);
}
.ws-tasks-header-actions {
  display: flex; align-items: center; gap: 8px;
}

/* View toggle (segmented control) */
.ws-view-toggle {
  display: inline-flex; padding: 3px; gap: 2px;
  background: var(--ws-soft); border-radius: 8px;
}
.ws-view-toggle button {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px; border: 0; background: transparent;
  font-size: 12.5px; font-weight: 500; color: var(--ws-ink-3);
  cursor: pointer; border-radius: 6px; font-family: var(--ws-sans);
}
.ws-view-toggle button.on {
  background: var(--ws-panel); color: var(--ws-ink);
  box-shadow: 0 1px 2px rgba(19,17,14,0.06);
}

.ws-tasks-hdr-link {
  display: inline-flex; align-items: center; gap: 5px;
  border: none; background: none; padding: 7px 12px;
  font-size: 13px; font-weight: 500; font-family: var(--ws-sans);
  color: var(--ws-ink-3); cursor: pointer; transition: color 0.12s;
}
.ws-tasks-hdr-link:hover { color: var(--ws-ink); }
.ws-tasks-hdr-primary {
  display: inline-flex; align-items: center; gap: 6px;
  border: 1px solid var(--ws-ink); border-radius: var(--ws-r-md);
  padding: 7px 16px; font-size: 13px; font-weight: 500;
  font-family: var(--ws-sans); color: var(--ws-ink);
  background: transparent; cursor: pointer; transition: all 0.15s;
}
.ws-tasks-hdr-primary:hover { background: var(--ws-ink); color: var(--ws-bg); }

/* ===== Task queue — body ===== */
.ws-tasks {
  padding: 0 32px 24px; max-width: 1100px; margin: 0 auto;
  width: 100%; box-sizing: border-box; overflow-y: auto; flex: 1; min-height: 0;
}
.ws-tasks-sticky-header {
  position: sticky; top: 0; z-index: 3; background: var(--ws-bg);
  padding-top: 20px; padding-bottom: 0;
}
.ws-tasks-empty { padding: 60px 20px; text-align: center; color: var(--ws-ink-4); font-size: 13px; }

/* Filter card container */
.ws-tq-filters {
  background: var(--ws-panel);
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-md);
  padding: 10px 12px;
  margin-bottom: 12px;
}
.ws-tq-filter-row {
  display: flex; flex-wrap: wrap; align-items: center;
  gap: 8px 10px; padding: 4px 0;
}
.ws-tq-filter-row + .ws-tq-filter-row {
  border-top: 1px dashed var(--ws-line);
  margin-top: 4px; padding-top: 8px;
}

/* Search inside filter card */
.ws-tasks-search {
  flex: 1; min-width: 240px;
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; background: var(--ws-soft);
  border: 1px solid transparent; border-radius: 8px;
  transition: border-color 0.12s;
}
.ws-tasks-search:focus-within { border-color: var(--ws-ink-5); background: var(--ws-panel); }
.ws-tasks-search input {
  flex: 1; background: transparent; border: none; outline: none;
  font-family: var(--ws-sans); font-size: 13px; color: var(--ws-ink);
}
.ws-tasks-search input::placeholder { color: var(--ws-ink-4); }

/* Filter chips */
.ws-tq-chip-group {
  display: inline-flex; align-items: center; gap: 4px; flex-wrap: wrap;
}
.ws-tq-chip-label {
  font-size: 10.5px; font-weight: 600; color: var(--ws-ink-4);
  text-transform: uppercase; letter-spacing: 0.08em; margin-right: 4px;
}
.ws-tq-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 10px; border: 1px solid var(--ws-line);
  background: var(--ws-panel); border-radius: 999px;
  font-family: var(--ws-sans); font-size: 12px; color: var(--ws-ink-2);
  cursor: pointer; transition: all 0.12s;
}
.ws-tq-chip:hover { border-color: var(--ws-ink-5); }
.ws-tq-chip.on {
  background: var(--ws-ink); border-color: var(--ws-ink); color: #e8f9f8;
}
.ws-tq-chip.on .ws-tq-ct { background: rgba(255,255,255,0.18); color: #e8f9f8; }
.ws-tq-chip.warn.on { background: var(--ws-warn); border-color: var(--ws-warn); color: #4A2A0A; }
.ws-tq-chip.ok.on { background: #2F7A4E; border-color: #2F7A4E; }
.ws-tq-chip.err.on { background: #C9462C; border-color: #C9462C; }
.ws-tq-chip.info.on { background: var(--ws-info); border-color: var(--ws-info); }
.ws-tq-ct {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 16px; padding: 0 5px;
  background: var(--ws-soft); border-radius: 8px;
  font-size: 10.5px; font-weight: 600; color: var(--ws-ink-4);
  font-variant-numeric: tabular-nums;
}
.ws-tq-chip-agent { padding: 3px 10px 3px 4px; }
.ws-tq-chip-agent .ws-agent-chip { pointer-events: none; }
.ws-tq-chip-agent.on {
  background: var(--ws-panel);
  border: 2px solid var(--ws-ink-2);
  padding: 2px 9px 2px 3px;
  color: var(--ws-ink);
}
.ws-tq-chip-ghost {
  border-color: transparent; color: var(--ws-ink-3); background: transparent;
  margin-left: auto;
}
.ws-tq-chip-ghost:hover { background: var(--ws-soft); color: var(--ws-ink); }
.ws-tq-chip-ghost:disabled { color: var(--ws-ink-5); cursor: default; }
.ws-tq-chip-ghost:disabled:hover { background: transparent; color: var(--ws-ink-5); }

/* GROUP BY + SORT controls row */
.ws-tasks-controls-row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap; margin-bottom: 10px; padding: 0 2px;
}
.ws-tasks-controls-left, .ws-tasks-controls-right {
  display: inline-flex; align-items: center; gap: 8px;
}
.ws-tq-segmented {
  display: inline-flex; padding: 2px; gap: 2px;
  background: var(--ws-soft); border-radius: 7px;
}
.ws-tq-segmented button {
  padding: 5px 10px; border: 0; background: transparent;
  font-family: var(--ws-sans); font-size: 12px; color: var(--ws-ink-3);
  cursor: pointer; border-radius: 5px;
}
.ws-tq-segmented button.on {
  background: var(--ws-panel); color: var(--ws-ink); font-weight: 600;
  box-shadow: 0 1px 2px rgba(19,17,14,0.06);
}

/* Table card */
.ws-tq-table {
  background: var(--ws-panel);
  border: 1px solid var(--ws-line);
  border-radius: 0 0 var(--ws-r-md) var(--ws-r-md);
  border-top: 0;
}

/* Table grid — 7 columns: dot + task + chain + status + duration + cost + when */
.ws-tbl-header, .ws-tbl-row {
  display: grid;
  grid-template-columns: 14px minmax(200px, 2fr) minmax(140px, 1.2fr) 80px 80px 70px minmax(130px, auto);
  gap: 14px; align-items: center; padding: 10px 14px;
}
.ws-tbl-header {
  font-size: 10.5px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.08em; color: var(--ws-ink-4);
  background: var(--ws-soft); border-bottom: 1px solid var(--ws-line);
  border-radius: var(--ws-r-md) var(--ws-r-md) 0 0;
}
.ws-tbl-header span:nth-child(n+5):nth-child(-n+6) { text-align: right; }
.ws-tbl-row {
  border-bottom: 1px solid var(--ws-line);
  cursor: pointer; transition: background 0.1s; font-size: 13px;
}
.ws-tbl-row:last-child { border-bottom: 0; }
.ws-tbl-row:hover { background: var(--ws-soft); }
.ws-tbl-row.needs-you { background: rgba(184,112,42,0.03); }

/* Status dots */
.ws-tq-dot {
  width: 8px; height: 8px; border-radius: 50%; background: var(--ws-ink-5);
}
.ws-tq-dot.todo { background: var(--ws-ink-5); }
.ws-tq-dot.in_progress { background: var(--ws-info); box-shadow: 0 0 0 3px rgba(59,107,168,0.2); }
.ws-tq-dot.blocked { background: var(--ws-warn); }
.ws-tq-dot.done { background: #2F7A4E; }
.ws-tq-dot.cancelled { background: var(--ws-ink-5); opacity: 0.5; }

/* Task cell */
.ws-tbl-cell-task { min-width: 0; }
.ws-tbl-task-top {
  display: flex; align-items: center; gap: 8px;
  font-weight: 500; color: var(--ws-ink);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ws-tbl-task-id {
  font-family: var(--ws-mono); font-size: 10.5px; color: var(--ws-ink-4);
  background: var(--ws-soft); padding: 1px 6px; border-radius: 4px;
  flex-shrink: 0;
}
.ws-tbl-task-title {
  font-weight: 500; color: var(--ws-ink); font-size: 13px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ws-tbl-task-sub {
  margin-top: 3px; font-size: 12px; color: var(--ws-ink-3);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ws-tasks-row-action { color: var(--ws-ink-2); font-weight: 500; }

/* Chain cell */
.ws-tbl-cell-chain { min-width: 0; justify-self: start; }
.ws-chain {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  font-size: 11.5px;
}
.ws-chain-arrow { color: var(--ws-ink-4); font-size: 11px; }

/* Status pill in table row */
.ws-tq-pill {
  display: inline-flex; align-items: center; padding: 2px 8px;
  border-radius: 999px; font-size: 11px; font-weight: 500;
  background: var(--ws-soft); color: var(--ws-ink-3);
  justify-self: start;
}
.ws-tq-pill.warn { background: rgba(184,112,42,0.12); color: var(--ws-warn); }
.ws-tq-pill.info { background: rgba(59,107,168,0.12); color: var(--ws-info); }
.ws-tq-pill.ok { background: rgba(47,122,78,0.12); color: #2F7A4E; }
.ws-tq-pill.err { background: rgba(201,70,44,0.12); color: #C9462C; }

/* Mono cells (tools, duration, cost) */
.ws-tbl-cell-mono {
  font-variant-numeric: tabular-nums;
  font-size: 12.5px; color: var(--ws-ink-2);
  text-align: right;
}

/* When cell */
.ws-tbl-cell-when {
  font-size: 12px; color: var(--ws-ink-3); white-space: nowrap;
  position: relative;
}
.ws-tbl-row-actions {
  display: none; position: absolute; right: 0; top: 50%;
  transform: translateY(-50%); background: var(--ws-soft);
  padding: 2px 4px; border-radius: var(--ws-r-sm);
}
.ws-tbl-row:hover .ws-tbl-row-actions { display: flex; gap: 4px; }
.ws-tasks-action-btn {
  display: grid; place-items: center;
  width: 26px; height: 26px; border-radius: var(--ws-r-sm);
  border: none; background: transparent; cursor: pointer;
  color: var(--ws-ink-4); transition: all 0.12s;
}
.ws-tasks-action-cancel:hover { color: var(--ws-err); background: rgba(184,74,62,0.08); }
.ws-tasks-action-retry:hover { color: var(--ws-info); background: rgba(59,107,168,0.08); }
.ws-tasks-action-btn:disabled { opacity: 0.4; cursor: default; }

/* Groups */
.ws-tq-group { }
.ws-tq-group + .ws-tq-group .ws-tq-group-head { border-top: 1px solid var(--ws-line); }
.ws-tq-group-head {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px 8px;
  font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.06em; color: var(--ws-ink-3);
  background: var(--ws-soft); border-bottom: 1px solid var(--ws-line);
  position: sticky; z-index: 2;
}
.ws-tq-group-ct {
  font-size: 10.5px; color: var(--ws-ink-4); font-weight: 500;
  font-variant-numeric: tabular-nums;
}
`;

export const runsPagination = `
/* ===== Pagination ===== */
.ws-tasks-pagination {
  display: flex; align-items: center; justify-content: center;
  gap: 12px; padding: 16px 0 8px;
}
.ws-tasks-page-btn {
  display: grid; place-items: center;
  width: 28px; height: 28px; border-radius: var(--ws-r-sm);
  border: 1px solid var(--ws-line); background: var(--ws-panel);
  cursor: pointer; color: var(--ws-ink-2); transition: all 0.12s;
}
.ws-tasks-page-btn:hover:not(:disabled) { background: var(--ws-soft); }
.ws-tasks-page-btn:disabled { opacity: 0.35; cursor: default; }
.ws-tasks-page-info {
  font-size: 12px; color: var(--ws-ink-3); font-variant-numeric: tabular-nums;
}
`;

export const runsActivityLog = `
/* ===== Activity log ===== */
.ws-activity { padding: 24px 32px; max-width: 1100px; margin: 0 auto; }
.ws-activity-filters { display: flex; align-items: center; gap: 12px; margin: 12px 0 16px; }
.ws-activity-filters .ws-tasks-tabs { flex-wrap: wrap; flex: 1; min-width: 0; }
.ws-activity-select {
  appearance: none; -webkit-appearance: none;
  background: var(--ws-panel) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23999'/%3E%3C/svg%3E") no-repeat right 10px center;
  border: 1px solid var(--ws-line); border-radius: var(--ws-r-sm);
  padding: 6px 28px 6px 12px; font-size: 12.5px; font-family: var(--ws-sans);
  color: var(--ws-ink-2); cursor: pointer; white-space: nowrap;
}
.ws-activity-select:hover { border-color: var(--ws-ink-4); color: var(--ws-ink); }
.ws-activity-select:focus { outline: none; border-color: var(--ws-ink-3); }
.ws-activity-row {
  display: grid; grid-template-columns: auto 1fr auto auto;
  gap: 10px; align-items: center; padding: 10px 8px;
  border-bottom: 1px solid var(--ws-line); cursor: pointer;
}
.ws-activity-row:hover { background: var(--ws-hover); }
.ws-activity-row-status {
  display: inline-flex; align-items: center; padding: 2px 8px;
  border-radius: 999px; font-size: 11px; font-weight: 500;
  background: var(--ws-soft); color: var(--ws-ink-3);
}
.ws-activity-row-status.running { background: rgba(59,107,168,0.12); color: var(--ws-info); }
.ws-activity-row-status.succeeded { background: rgba(47,122,78,0.12); color: var(--ws-ok); }
.ws-activity-row-status.failed { background: rgba(184,74,62,0.12); color: var(--ws-err); }
.ws-activity-row-status.awaiting_approval { background: rgba(184,112,42,0.12); color: var(--ws-warn); }
.ws-activity-row-status.awaiting_input { background: rgba(184,112,42,0.12); color: var(--ws-warn); }
.ws-activity-row-status.cancelled { background: var(--ws-soft); color: var(--ws-ink-4); }
.ws-activity-badge {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 2px 7px; border-radius: 999px;
  font-size: 10px; font-weight: 500; flex-shrink: 0;
  white-space: nowrap;
}
.ws-activity-badge.delegated { background: rgba(59,107,168,0.10); color: var(--ws-info); }
.ws-activity-badge.approval { background: rgba(184,112,42,0.10); color: var(--ws-warn); }
.ws-reply-preview {
  padding: 10px 14px; margin: 0 0 8px;
  background: var(--ws-soft); border-radius: var(--ws-r-sm);
}
.ws-reply-preview-text {
  margin: 0; font-size: 13px; line-height: 1.55;
  color: var(--ws-ink-2); word-break: break-word;
  white-space: pre-wrap;
}
.ws-reply-preview-link {
  display: inline-flex; align-items: center; gap: 4px;
  margin-top: 8px; padding: 0; border: 0; background: none;
  font-size: 12px; font-weight: 500; color: var(--ws-ink-3);
  cursor: pointer; font-family: var(--ws-sans);
}
.ws-reply-preview-link:hover { color: var(--ws-ink); }
.ws-outcome-preview { display: flex; flex-direction: column; gap: 6px; }
.ws-outcome-summary {
  margin: 0; font-size: 13px; line-height: 1.5; color: var(--ws-ink-1);
  white-space: pre-wrap; word-break: break-word;
}
.ws-outcome-toggle {
  display: inline-block; margin-top: 4px; padding: 0; border: none; background: none;
  font-size: 12px; color: var(--ws-ink-4); cursor: pointer; font-family: var(--ws-sans);
}
.ws-outcome-toggle:hover { color: var(--ws-ink-2); }
.ws-outcome-question {
  margin: 0; font-size: 12.5px; line-height: 1.5; color: var(--ws-warn);
  display: flex; align-items: flex-start; gap: 5px;
}
.ws-work-items-list { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 2px; }
/* Right-align the delegation chip list under a message (matches the right-aligned
   delegated-result tag in the header). */
.ws-work-items-list--end { justify-content: flex-end; }
.ws-work-item-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px; border-radius: 999px; font-size: 11.5px; font-weight: 500;
  background: rgba(47,122,78,0.10); color: var(--ws-ok);
  white-space: nowrap; max-width: 280px; overflow: hidden;
}
/* Truncate the label (not the whole flex row) so a long title gets an ellipsis
   while the leading/trailing icons stay visible and the pill keeps its shape.
   min-width:0 lets the label shrink below its content in the flex row. */
.ws-work-item-chip > span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* Delegation chip shares the neutral palette of the delegated-result tag. */
.ws-work-item-chip--delegation {
  background: var(--ws-soft); color: var(--ws-ink-4);
}
button.ws-work-item-chip {
  border: 0; cursor: pointer; font-family: inherit;
}
button.ws-work-item-chip--delegation {
  border: 1px solid var(--ws-line-2);
}
button.ws-work-item-chip--clickable:hover {
  filter: brightness(0.96);
}
/* Banner above a delegated-work *result* message in the parent conversation —
   makes it unmistakable the summary came back from a background delegated run,
   and opens that child conversation in the drawer on click. */
.ws-delegation-result-header {
  display: flex; align-items: center; gap: 6px;
  width: 100%; margin: 0 0 8px; padding: 7px 12px;
  border: 1px solid var(--ws-line-2); border-radius: var(--ws-r-md);
  background: rgba(59,107,168,0.06);
  font-family: inherit; font-size: 12px; font-weight: 500;
  color: var(--ws-ink-3); cursor: pointer; text-align: left;
  transition: background 0.12s, border-color 0.12s;
}
.ws-delegation-result-header:hover { background: rgba(59,107,168,0.10); border-color: var(--ws-info); }
.ws-delegation-result-label { color: var(--ws-ink-4); }
.ws-delegation-result-dot { color: var(--ws-ink-5); }
.ws-delegation-result-agent { color: var(--ws-ink); font-weight: 600; }
/* Compact inline variant rendered in the message header row (right of name/time). */
.ws-delegation-result-tag {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 1px 8px; border-radius: 999px;
  border: 1px solid var(--ws-line-2); background: var(--ws-soft);
  font-family: inherit; font-size: 11px; font-weight: 500; line-height: 1.7;
  color: var(--ws-ink-4); cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.ws-delegation-result-tag:hover { background: var(--ws-hover); border-color: var(--ws-info); color: var(--ws-ink-2); }
/* Prominent full-width banner rendered BELOW the message that launched a
   background task, so a delegation the user just approved is unmistakable and
   click-to-open right where they're looking — not a small header pill. Warm
   accent while running, neutral/info once it finishes. WEB-7850. */
.ws-delegation-launch-banner {
  display: flex; align-items: center; gap: 8px;
  width: 100%; margin: 8px 0 0; padding: 9px 12px;
  border: 1px solid var(--ws-line-2); border-radius: var(--ws-r-md, 8px);
  background: rgba(214,158,46,0.08);
  font-family: inherit; font-size: 12.5px; font-weight: 500;
  color: var(--ws-ink-2); cursor: pointer; text-align: left;
  transition: background 0.12s, border-color 0.12s;
}
.ws-delegation-launch-banner:hover { background: rgba(214,158,46,0.14); border-color: var(--ws-yellow); }
.ws-delegation-launch-banner .ws-launch-label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ws-delegation-launch-banner--done { background: rgba(59,107,168,0.06); }
.ws-delegation-launch-banner--done:hover { background: rgba(59,107,168,0.10); border-color: var(--ws-info); }
.ws-summary-draft-panel {
  flex-basis: 100%;
  margin-top: 4px;
  padding: 10px 12px;
  border-radius: var(--ws-r-sm);
  background: var(--ws-soft);
  border: 1px solid var(--ws-line);
  display: flex; flex-direction: column; gap: 8px;
}
.ws-summary-draft-section-head {
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--ws-ink-4);
}
.ws-summary-draft-section-head--risk { color: var(--ws-red); }
.ws-summary-draft-text {
  margin: 0; font-size: 12.5px; line-height: 1.5;
  color: var(--ws-ink-1); white-space: pre-wrap; word-break: break-word;
}
.ws-summary-draft-watchout { display: flex; flex-direction: column; gap: 6px; }
.ws-summary-draft-watchout-item {
  display: flex; flex-direction: column; gap: 2px;
}
.ws-summary-draft-watchout-issue {
  font-size: 12.5px; font-weight: 500; color: var(--ws-ink-1);
}
.ws-summary-draft-watchout-action {
  font-size: 12px; color: var(--ws-ink-4);
}
/* Email-draft expand panel — preview of subject/body + resolved signature.
 * The signature HTML comes from the server (already templated), and is
 * rendered verbatim so the rep sees exactly what will be sent. */
.ws-email-draft-panel {
  flex-basis: 100%;
  margin: 4px 0 8px;
  padding: 10px 12px;
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-sm);
  background: var(--ws-soft);
  font-size: 12px;
  display: flex; flex-direction: column;
}
.ws-email-draft-subject { font-weight: 600; margin-bottom: 6px; color: var(--ws-ink-1); }
.ws-email-draft-body { white-space: pre-wrap; margin: 0 0 8px; color: var(--ws-ink-1); }
.ws-email-draft-signature {
  border-top: 1px dashed var(--ws-line);
  padding-top: 6px;
  margin-bottom: 8px;
  color: var(--ws-ink-4);
}
.ws-email-draft-signature p { margin: 0 0 2px 0; }
.ws-email-draft-signature img { max-width: 200px; height: auto; }
.ws-contact-diff-panel { gap: 10px; }
.ws-contact-diff-list { display: flex; flex-direction: column; gap: 4px; }
.ws-contact-diff-row {
  display: grid; grid-template-columns: 60px 1fr auto 1fr;
  gap: 6px; align-items: baseline;
  font-size: 12.5px;
}
.ws-contact-diff-label { font-weight: 600; color: var(--ws-ink-1); }
.ws-contact-diff-current { color: var(--ws-ink-4); }
.ws-contact-diff-arrow { color: var(--ws-ink-4); }
.ws-contact-diff-next { color: var(--ws-ink-1); font-weight: 500; }
.ws-contact-diff-error {
  font-size: 12px; color: var(--ws-red);
}
.ws-contact-diff-actions {
  display: flex; justify-content: flex-end; gap: 6px;
}
.ws-activity-row-time { font-size: 12px; color: var(--ws-ink-4); }
.ws-activity-events-inline {
  background: var(--ws-soft); border-radius: var(--ws-r-sm);
  padding: 12px; margin: 4px 0 12px;
}
`;
